// Etiket drawer'ı: adlandırma, üniteye bağlama ve son ölçümler.
//
// Etiket = paketteki `values` sözlüğünün bir anahtarı (§3.2). Ajan anahtarı
// serbest ad veriyor (`gd_p01_mbar`), tesis onu insan diline burada çeviriyor.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { sayiyaCevirBos, tr, trGiris, trTarihSaat } from '../../lib/trFormat'
import { uniteler as uniteApi } from '../uretim/api'
import { kaliteRozeti, noktaMetni, sonDegerMetni, yasMetni } from './bicim'
import { entegrasyon, type EtiketSatiri } from './api'

/** Seride gösterilen nokta sayısı — panel bir liste, grafik değil. */
const SERI_ADEDI = 50

export default function EtiketDrawer({
  etiket,
  yazabilir,
}: {
  etiket: EtiketSatiri
  yazabilir: boolean
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()

  const [ad, setAd] = useState(etiket.ad ?? '')
  const [birim, setBirim] = useState(etiket.birim ?? '')
  const [uniteId, setUniteId] = useState(etiket.uniteId ?? '')
  const [parametreId, setParametreId] = useState(etiket.beyanParametresiId ?? '')
  const [carpan, setCarpan] = useState(trGiris(etiket.carpan))
  const [ofset, setOfset] = useState(trGiris(etiket.ofset))

  // Ünite listesi ekran 05 ile aynı önbellekten okunuyor — ek istek doğurmaz.
  const { data: uniteListesi = [] } = useQuery({
    queryKey: ['uniteler'],
    queryFn: () => uniteApi.liste(),
  })

  const { data: uniteDetay } = useQuery({
    queryKey: ['unite', uniteId],
    queryFn: () => uniteApi.detay(uniteId),
    enabled: !!uniteId,
  })

  const { data: seri, isLoading: seriYukleniyor } = useQuery({
    queryKey: ['entegrasyon-seri', etiket.id],
    queryFn: () => entegrasyon.seri(etiket.id, SERI_ADEDI),
  })

  const carpanSayi = sayiyaCevirBos(carpan)
  // Sunucu 0'ı 409 ile reddediyor (dönüşüm her okumayı sıfırlar); kullanıcıyı
  // istek atmadan durduruyoruz ama kuralın kendisi sunucuda kalıyor.
  const carpanGecersiz = carpanSayi === 0

  const kaydet = useMutation({
    mutationFn: () =>
      entegrasyon.etiketGuncelle(etiket.id, {
        ad: ad.trim() || null,
        birim: birim.trim() || null,
        uniteId: uniteId || null,
        // Ünite temizlenirse parametre de düşer: parametre üniteye ait, ünitesiz
        // bağ ölü bir kayıt olurdu.
        beyanParametresiId: uniteId ? parametreId || null : null,
        carpan: carpanSayi,
        ofset: sayiyaCevirBos(ofset),
      }),
    onSuccess: (e) => {
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-sayfa', e.sayfaId] })
      toast(
        e.uniteId
          ? `${e.anahtar} · kaydedildi ve üniteye bağlandı`
          : `${e.anahtar} · kaydedildi (bağlı değil)`,
      )
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Etiket kaydedilemedi'),
  })

  const kalite = kaliteRozeti(etiket.sonKalite)
  const secilenUnite = uniteListesi.find((u) => u.id === uniteId)

  return (
    <>
      <DrawerGovde>
        <div className="mini-list">
          <div className="mini-li">
            <span>Ajan anahtarı</span>
            <span className="vv">{etiket.anahtar}</span>
          </div>
          <div className="mini-li">
            <span>Tür</span>
            <span className="vv">
              {etiket.tur === 'sayisal' ? 'OCR sayısı' : 'Renk-durum'}
            </span>
          </div>
          <div className="mini-li">
            <span>Son değer</span>
            <span className="vv">
              {sonDegerMetni(etiket)}
              {etiket.birim ? ` ${etiket.birim}` : ''}
            </span>
          </div>
          <div className="mini-li">
            <span>Kalite</span>
            <span className={`badge ${kalite.sinif}`} style={{ marginLeft: 'auto' }}>
              {kalite.etiket}
            </span>
          </div>
          <div className="mini-li">
            <span>Son okuma</span>
            <span className="vv">
              {etiket.sonZaman ? trTarihSaat(etiket.sonZaman) : '—'}
              {etiket.sonZamanYasiSn !== null ? ` · ${yasMetni(etiket.sonZamanYasiSn)}` : ''}
            </span>
          </div>
          <div className="mini-li">
            <span>Ölçüm sayısı</span>
            <span className="vv">{tr(etiket.olcumSayisi, 0)}</span>
          </div>
        </div>

        <div className="sect-t" style={{ marginTop: 12, paddingLeft: 0 }}>
          Adlandırma
        </div>
        <div className="fgrid">
          <div className="fld">
            <label>Ad</label>
            <input
              className="in"
              value={ad}
              readOnly={!yazabilir}
              placeholder={etiket.anahtar}
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Birim</label>
            <input
              className="in mono"
              value={birim}
              readOnly={!yazabilir}
              placeholder="mbar"
              onChange={(e) => setBirim(e.target.value)}
            />
          </div>
        </div>

        <div className="sect-t" style={{ marginTop: 12, paddingLeft: 0 }}>
          Üniteye bağla
        </div>

        {!uniteId && (
          <div className="rule" style={{ marginBottom: 8 }}>
            <b>Bağlı değil</b>
            <span>
              Bu okuma yalnız entegrasyon ekranında görünüyor; bir üniteye ve beyan
              parametresine bağlarsanız hangi göstergeye karşılık geldiği kayda geçer.
            </span>
          </div>
        )}

        <div className="fgrid">
          <div className="fld">
            <label>Ünite</label>
            <select
              className="in"
              value={uniteId}
              disabled={!yazabilir}
              onChange={(e) => {
                setUniteId(e.target.value)
                // Ünite değişince eski parametre başka üniteye ait kalırdı.
                setParametreId('')
              }}
            >
              <option value="">— bağlı değil —</option>
              {uniteListesi.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.kod} · {u.ad}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label>Beyan parametresi</label>
            <select
              className="in"
              value={parametreId}
              disabled={!yazabilir || !uniteId}
              title={uniteId ? undefined : 'Önce ünite seçin'}
              onChange={(e) => setParametreId(e.target.value)}
            >
              <option value="">— seçilmedi —</option>
              {(uniteDetay?.parametreler ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.ad}
                  {p.birim ? ` (${p.birim})` : ''}
                </option>
              ))}
            </select>
            {secilenUnite && (uniteDetay?.parametreler ?? []).length === 0 && (
              <div className="fld-hint">
                {secilenUnite.kod} ünitesinde beyan parametresi tanımlı değil.
              </div>
            )}
          </div>
        </div>

        <div className="fgrid" style={{ marginTop: 8 }}>
          <div className={carpanGecersiz ? 'fld err' : 'fld'}>
            <label>Çarpan</label>
            <input
              className="in mono"
              value={carpan}
              readOnly={!yazabilir}
              placeholder="1"
              onChange={(e) => setCarpan(e.target.value)}
            />
            <div className="fld-hint">
              {carpanGecersiz
                ? 'Çarpan 0 olamaz — dönüşüm her okumayı sıfırlar. Boş bırakın.'
                : 'Boş = dönüşüm yok'}
            </div>
          </div>
          <div className="fld">
            <label>Ofset</label>
            <input
              className="in mono"
              value={ofset}
              readOnly={!yazabilir}
              placeholder="0"
              onChange={(e) => setOfset(e.target.value)}
            />
            <div className="fld-hint">değer × çarpan + ofset</div>
          </div>
        </div>

        {/* Söz vermediğimiz bir davranışı ima etmemek için açıkça yazıyoruz:
            bağlama şu an yalnız KAYITTIR, okumayı beyan gridine akıtan bir yol
            henüz yok (akıtma ayrı bir karar ve ayrı bir faz). */}
        <div className="rule" style={{ marginTop: 10 }}>
          <b>Not</b>
          <span>
            Bağlama şimdilik yalnız kaydediliyor: SCADA okuması beyan gridine ya da
            ünite göstergesine otomatik akmıyor.
          </span>
        </div>

        <div className="sect-t" style={{ marginTop: 12, paddingLeft: 0 }}>
          Son ölçümler ({SERI_ADEDI} nokta)
        </div>
        <div className="mini-list">
          {seriYukleniyor && <div className="mini-li">Yükleniyor…</div>}
          {!seriYukleniyor && (seri?.noktalar.length ?? 0) === 0 && (
            <div className="mini-li" style={{ color: 'var(--faint)' }}>
              Henüz veri akmadı.
            </div>
          )}
          {/* Sunucu ARTAN sırada döndürüyor (grafik için); listede en yeni üstte
              olmalı, bu yüzden kopya diziyi ters çeviriyoruz. */}
          {[...(seri?.noktalar ?? [])].reverse().map((n, i) => {
            const k = kaliteRozeti(n.kalite)
            return (
              <div className="mini-li" key={`${n.zaman}-${i}`}>
                {/* .dt 40px sabit — tarih+saat sığmıyor, genişletiliyor. */}
                <span className="dt" style={{ width: 96 }}>{trTarihSaat(n.zaman)}</span>
                <span className={`badge ${k.sinif}`}>{k.etiket}</span>
                {n.yanip ? <span className="badge b-red">Yanıp sönüyor</span> : null}
                {n.kararli === false ? <span className="badge b-gray">Kararsız</span> : null}
                <span className="vv">{noktaMetni(n)}</span>
              </div>
            )
          })}
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Kapat
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!yazabilir || carpanGecersiz || kaydet.isPending}
          title={
            !yazabilir
              ? 'Bu modülde yazma yetkiniz yok'
              : carpanGecersiz
                ? 'Çarpan 0 olamaz'
                : undefined
          }
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}
